import React, { useState } from 'react';
import { addMonths, addWeeks, endOfWeek, format, startOfWeek } from 'date-fns';
import { CalendarRangeIcon, ChevronLeftIcon, ChevronRightIcon, UsersIcon } from 'lucide-react';
import { TeamCalendar } from '../components/schedule/TeamCalendar';
import type { CalendarMode } from '../components/schedule/TeamCalendar';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { FilterSelect } from '../components/ui/FilterSelect';
import { PageHeader } from '../components/ui/PageHeader';
import { SELF_ID } from '../data/selfService';
import { useHRStore } from '../hooks/useHRStore';
import type { AppView } from '../types/selfService';
import { TODAY } from '../utils/format';
import { holidayApplies } from '../utils/holidays';
import { TEAMS_BY_GROUP, WORK_GROUPS, groupFor, teamFor } from '../utils/teams';
import type { WorkGroup } from '../utils/teams';
export function Schedule({
  view
}: {
  view: AppView;
}) {
  const {
    employees,
    holidays,
    requests
  } = useHRStore();
  const isAdmin = view === 'admin';
  const [mode, setMode] = useState<CalendarMode>('month');
  const [anchor, setAnchor] = useState(TODAY);
  const [group, setGroup] = useState<'All' | WorkGroup>('All');
  const [team, setTeam] = useState('All');
  const me = employees.find(e => e.id === SELF_ID);
  const myTeam = me ? teamFor(me) : '';
  const active = employees.filter(e => e.status !== 'Terminated');
  const people = isAdmin ? active.filter(e => (group === 'All' || groupFor(e) === group) && (team === 'All' || teamFor(e) === team)) : active.filter(e => me && e.department === me.department && teamFor(e) === myTeam);
  const visibleHolidays = isAdmin ? holidays : holidays.filter(h => me && holidayApplies(h, me.policyId, me.location));
  const teamOptions = group === 'All' ? WORK_GROUPS.flatMap(g => TEAMS_BY_GROUP[g]) : TEAMS_BY_GROUP[group];
  const step = (dir: 1 | -1) => setAnchor(a => mode === 'month' ? addMonths(a, dir) : addWeeks(a, dir));
  const title = mode === 'month' ? format(anchor, 'MMMM yyyy') : `${format(startOfWeek(anchor), 'MMM d')} – ${format(endOfWeek(anchor), 'MMM d, yyyy')}`;
  return <div data-id="element-1528" data-mp="2qfc2dmdghvb">
      <PageHeader eyebrow="Company" title="Team schedule" description={isAdmin ? "Who's out, holidays and work anniversaries across the company." : `Who's out, holidays and anniversaries for the ${myTeam} team.`} data-id="element-1529" data-mp="2pw2bk6vkzsd" />

      <Card bodyClassName="p-4 md:p-6" data-id="element-1530" data-mp="co36f2g2fncy">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-1531" data-mp="gpomewnwchrd">
          <div className="flex flex-wrap items-center gap-3" data-id="element-1532" data-mp="lgwvfrlbe140">
            <div className="inline-flex items-center gap-1 rounded-xl bg-canvas p-1 ring-1 ring-inset ring-slate-200" data-id="element-1533" data-mp="liq75lasbbiv">
              <button type="button" onClick={() => step(-1)} aria-label={`Previous ${mode}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-navy transition hover:bg-white" data-id="element-1534" data-mp="vet9os9jtc6c">
                <ChevronLeftIcon className="h-4 w-4" data-id="element-1535" data-mp="2kvyjg8z95v2p" />
              </button>
              <button type="button" onClick={() => setAnchor(TODAY)} className="rounded-lg px-3 py-1.5 text-xs font-medium text-navy transition hover:bg-white" data-id="element-1536" data-mp="2kx9jpythokp2">
                Today
              </button>
              <button type="button" onClick={() => step(1)} aria-label={`Next ${mode}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-navy transition hover:bg-white" data-id="element-1537" data-mp="2azv7kbelmi2k">
                <ChevronRightIcon className="h-4 w-4" data-id="element-1538" data-mp="33loackzw9wpc" />
              </button>
            </div>
            <h2 className="text-lg font-semibold text-navy" aria-live="polite" data-id="element-1539" data-mp="2yo4ytsukzy13">
              {title}
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-id="element-1540" data-mp="2oxjurpw521as">
            {isAdmin ? <>
                <FilterSelect label="Group" allLabel="Everyone" value={group} options={WORK_GROUPS} onChange={v => {
              setGroup(v as 'All' | WorkGroup);
              setTeam('All');
            }} className="w-52" data-id="element-1541" data-mp="3mcnyjjzkf2hk" />
                <FilterSelect label="Team" allLabel="All teams" value={team} options={teamOptions} onChange={setTeam} className="w-48" data-id="element-1542" data-mp="370xzsdr76y9y" />
              </> : <Badge tone="navy" icon={UsersIcon} data-id="element-1543" data-mp="2x5mtmjtuuzdt">
                {myTeam} team · {people.length}
              </Badge>}
            <div className="flex rounded-xl bg-canvas p-1 ring-1 ring-inset ring-slate-200" role="group" aria-label="Calendar view" data-id="element-1544" data-mp="3kx1s21iigwjs">
              {(['month', 'week'] as CalendarMode[]).map(m => <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m} className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition ${mode === m ? 'bg-white text-navy shadow-sm' : 'text-muted hover:text-navy'}`} data-id="element-1545" data-mp="3g3z6afjz6re5">
                  {m}
                </button>)}
            </div>
          </div>
        </div>

        {isAdmin && <p className="mb-4 flex items-center gap-2 text-xs text-muted" data-id="element-1546" data-mp="26v4b4ifhqdxp">
            <CalendarRangeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1547" data-mp="26uay4zpxus74" />
            Showing <span className="font-semibold text-navy" data-id="element-1548" data-mp="21wja4gsezq87">{people.length}</span> {people.length === 1 ? 'person' : 'people'}
            {group !== 'All' ? ` in ${group}` : ''}
            {team !== 'All' ? ` · ${team}` : ''}
          </p>}

        <div className="overflow-x-auto" data-id="element-1549" data-mp="1n74zz6h0l4rr">
          <div className="min-w-[760px]" data-id="element-1550" data-mp="1n7wjhixc7c1o">
            <TeamCalendar anchor={anchor} mode={mode} people={people} holidays={visibleHolidays} requests={requests} showPending={isAdmin} highlightId={isAdmin ? undefined : SELF_ID} data-id="element-1551" data-mp="1n4s923ta3k2k" />
          </div>
        </div>
      </Card>
    </div>;
}